/**
 * API Endpoints - Simple and Clean
 * No over-engineering, just simple string constants and functions
 */
import { ID } from "./types";

// Simple endpoint constants and functions - no classes needed
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: "/auth/login",
    SIGNUP: "/auth/signup",
    REFRESH: "/auth/refresh",
    LOGOUT: "/auth/logout",
    PROFILE: "/auth/profile",
    SESSION: "/auth/get-session", // Better Auth's session endpoint
    SESSIONS: "/auth/sessions",
    REVOKE_SESSION: (sessionId: string) => `/auth/sessions/${sessionId}`,
    TEACHER_INVITATIONS: "/auth/teacher-invitations",
  },

  PRIVACY_POLICY: {
    CURRENT: "/privacy-policy/current",
    ACCEPT: "/privacy-policy/accept",
    ME: "/privacy-policy/me",
    ADMIN_LIST: "/privacy-policy/admin/versions",
    ADMIN_CREATE: "/privacy-policy/admin",
    // NOTE: PATCH /admin/:id was removed — edits always create a new revision via POST
  },

  // User endpoints
  USERS: "/users",
  USER_BY_ID: (id: ID) => `/users/${id}`,
  USER_BAN: (id: ID) => `/users/${id}/ban`,
  USER_DETAIL: (id: ID) => `/users/${id}/detail`,
  USER_RESET_PASSWORD: (id: ID) => `/users/${id}/reset-password`,

  // Student endpoints
  STUDENT_DETAILS: (id: ID) => `/student/${id}/details`,

  // Course endpoints
  COURSES: "/courses",
  COURSE_BY_ID: (id: ID) => `/courses/${id}`,
  TEACHER_COURSES: (teacherId: ID) => `/courses/teacher/${teacherId}`,
  COURSE_BY_CODE: (code: string) => `/courses/code/${code}`,

  // College endpoints
  COLLEGES: "/colleges",
  COLLEGE_BY_ID: (id: string) => `/colleges/${id}`,
  COLLEGES_BULK: "/colleges/bulk",

  // CMS endpoints
  CMS_BLOGS: "/cms/blogs",
  CMS_BLOG_BY_ID: (id: ID) => `/cms/blogs/${id}`,
  CMS_BLOG_TOGGLE_PUBLISH: (id: ID) => `/cms/blogs/${id}/publish`,
  CMS_BLOG_COVER_IMAGE: (id: ID) => `/cms/blogs/${id}/cover-image`,
  CMS_TESTIMONIALS: "/cms/testimonials",
  CMS_TESTIMONIAL_BY_ID: (id: ID) => `/cms/testimonials/${id}`,
  CMS_TESTIMONIAL_TOGGLE_PUBLISH: (id: ID) => `/cms/testimonials/${id}/publish`,
  CMS_TESTIMONIAL_AVATAR_IMAGE: (id: ID) =>
    `/cms/testimonials/${id}/avatar-image`,
  CMS_VIDEOS: "/cms/videos",
  CMS_VIDEO_BY_ID: (id: ID) => `/cms/videos/${id}`,
  CMS_VIDEO_TOGGLE_PUBLISH: (id: ID) => `/cms/videos/${id}/publish`,

  // Analytics endpoints
  ANALYTICS: {
    PLATFORM_OVERVIEW: "/analytics/platform-overview",
    ENGAGEMENT: "/analytics/engagement",
    ACTIVITIES: "/analytics/activities",
  },

  // Admin dashboard
  ADMIN: {
    DASHBOARD: "/admin/dashboard",
  },

  // Teacher endpoints
  TEACHER: {
    PROFILE: "/teacher/me",
    COURSES: "/teacher/courses",
    CREATE_COURSE: "/teacher/courses",
    COURSE_BY_CODE: (code: string) => `/teacher/courses/${code}`,
    UPDATE_COURSE: (code: string) => `/teacher/courses/${code}`,
    PUBLISH_COURSE: (code: string) => `/teacher/courses/${code}/publish`,
    FEATURED_COURSE: (code: string) => `/teacher/courses/${code}/featured`,

    // Student endpoints
    STUDENTS: "/teacher/students",
    STUDENT_COUNTS: "/teacher/students/count-by-course",
    PAUSE_STUDENT_ENROLLMENT: (enrollmentId: string) =>
      `/teacher/students/${enrollmentId}/pause`,
    RESUME_STUDENT_ENROLLMENT: (enrollmentId: string) =>
      `/teacher/students/${enrollmentId}/resume`,
    AUDIT_ENROLLMENTS: "/teacher/audit/enrollments",
    AUDIT_ENROLLMENTS_SUMMARY: "/teacher/audit/enrollments/summary",
    STUDENT_ENROLLMENTS: (studentId: string) =>
      `/teacher/students/${studentId}/enrollments`,
    STUDENT_DETAIL: (studentId: string) =>
      `/teacher/students/${studentId}/detail`,

    // Module endpoints
    COURSE_MODULES: (courseCode: string) =>
      `/teacher/courses/${courseCode}/modules`,
    MODULE_BY_CODE: (courseCode: string, moduleCode: string) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}`,
    MOVE_MODULE: (courseCode: string, moduleCode: string) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/move`,
    REORDER_MODULES: (courseCode: string) =>
      `/teacher/courses/${courseCode}/modules/reorder`,
    COURSE_MODULES_BY_ID: (courseId: ID) => `/courses/${courseId}/modules`,
    COURSE_MODULE_BY_ID: (courseId: ID, moduleId: ID) =>
      `/courses/${courseId}/modules/${moduleId}`,
    COURSE_MODULE_CONTENTS_BY_ID: (courseId: ID, moduleId: ID) =>
      `/courses/${courseId}/modules/${moduleId}/contents`,

    // Content endpoints
    MODULE_CONTENTS: (courseCode: string, moduleCode: string) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/contents`,
    UPLOAD_CONTENT: (courseCode: string, moduleCode: string) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content`,
    MOVE_CONTENT: (courseCode: string, moduleCode: string, contentId: ID) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/${contentId}/move`,
    UPDATE_CONTENT: (courseCode: string, moduleCode: string, contentId: ID) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/${contentId}`,
    INIT_VIDEO_UPLOAD: (courseCode: string, moduleCode: string) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/video/init`,
    VIDEO_PART_URL: (
      courseCode: string,
      moduleCode: string,
      videoAssetId: ID,
      partNumber: number,
    ) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/video/${videoAssetId}/parts/${partNumber}/url`,
    VIDEO_PART_CONFIRM: (
      courseCode: string,
      moduleCode: string,
      videoAssetId: ID,
      partNumber: number,
    ) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/video/${videoAssetId}/parts/${partNumber}/confirm`,
    COMMIT_VIDEO_UPLOAD: (courseCode: string, moduleCode: string) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/video/commit`,
    VIDEO_UPLOAD_STATUS: (
      courseCode: string,
      moduleCode: string,
      videoAssetId: ID,
    ) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/video/${videoAssetId}/status`,
    VIDEO_UPLOAD_ABORT: (
      courseCode: string,
      moduleCode: string,
      videoAssetId: ID,
    ) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/video/${videoAssetId}/abort`,
    DELETE_CONTENT: (courseCode: string, moduleCode: string, contentId: ID) =>
      `/teacher/courses/${courseCode}/modules/${moduleCode}/content/${contentId}`,
    COURSE_CONTENTS: (courseId: ID) => `/courses/${courseId}/contents`,
    COURSE_CONTENT_BY_ID: (contentId: ID) => `/courses/contents/${contentId}`,

    // Course deletion endpoint
    DELETE_COURSE: (courseCode: string) => `/teacher/courses/${courseCode}`,
    UPLOAD_COVER_IMAGE: (courseCode: string) =>
      `/teacher/courses/${courseCode}/cover-image`,
    DELETE_COVER_IMAGE: (courseCode: string) =>
      `/teacher/courses/${courseCode}/cover-image`,

    // Profile avatar endpoints
    PROFILE_IMAGE: "/teacher/profile/avatar",
    PROFILE_IMAGE_UPLOAD: "/teacher/profile/avatar",
    PROFILE_IMAGE_DELETE: "/teacher/profile/avatar",

    // Profile details & verification
    PROFILE_UPDATE: "/teacher/profile",
    ID_PROOF_UPLOAD: "/teacher/profile/id-proof",
    CERTIFICATE_UPLOAD: "/teacher/profile/certificate",

    // Coupon endpoints
    COUPONS: "/coupons",
    COUPON_BY_ID: (id: string) => `/coupons/${id}`,
    COUPON_PATCH: (id: string) => `/coupons/${id}`,
    MY_COUPONS: "/coupons",
    ACTIVATE_COUPON: (id: string) => `/coupons/${id}/activate`,
    DEACTIVATE_COUPON: (id: string) => `/coupons/${id}/deactivate`,
    VALIDATE_COUPON: "/coupons/validate",
    APPLY_COUPON: "/coupons/apply",
    REVERSE_REDEMPTION: (redemptionId: string) =>
      `/coupons/reverse/${redemptionId}`,
    COUPON_STATS: "/coupons/stats",

    // Dashboard endpoint
    DASHBOARD: "/teacher/dashboard",
    DASHBOARD_COURSES: "/teacher/dashboard/courses",

    // Notification endpoints
    NOTIFY_LIVE_SESSION: "/teacher/notifications/live-session",

    // Teacher-scoped orders
    ORDERS: "/teacher/orders",
  },

  // Broadcast endpoints (multi-channel announcements)
  BROADCASTS: {
    ADMIN: "/admin/broadcasts",
    TEACHER: "/teacher/broadcasts",
  },

  // Media endpoints
  MEDIA: {
    ACCESS: (mediaAssetId: ID) => `/media/${mediaAssetId}/access`,
    VIDEO_STATUS: (mediaAssetId: ID) => `/media/videos/${mediaAssetId}/status`,
    VIDEO_STREAM: (mediaAssetId: ID) => `/media/videos/${mediaAssetId}/stream`,
    VIDEO_MANIFEST: (mediaAssetId: ID) =>
      `/media/videos/${mediaAssetId}/manifest.m3u8`,
    VIDEO_SEGMENT: (mediaAssetId: ID, segmentName: string) =>
      `/media/videos/${mediaAssetId}/segment/${segmentName}`,
    VIDEO_ASSET_BY_ID: (videoAssetId: ID) => `/media/videos/${videoAssetId}`,
    VIDEO_ASSET_LIFECYCLE: (videoAssetId: ID) =>
      `/media/videos/${videoAssetId}/lifecycle`,
    VIDEO_ASSETS_ADMIN: "/media/videos/admin",
  },

  // MCQ endpoints
  MCQ: {
    MANAGE_SETS: "/mcq/manage/sets",
    MANAGE_SET_BY_ID: (id: ID) => `/mcq/manage/sets/${id}`,
    CREATE_SET: "/mcq/manage/sets",
    UPDATE_SET: (id: ID) => `/mcq/manage/sets/${id}`,
    DELETE_SET: (id: ID) => `/mcq/manage/sets/${id}`,
    DUPLICATE_SET: (id: ID) => `/mcq/manage/sets/${id}/duplicate`,
    ARCHIVE_SET: (id: ID) => `/mcq/manage/sets/${id}/archive`,

    // Question endpoints
    CREATE_QUESTION: (setId: ID) => `/mcq/manage/sets/${setId}/questions`,
    UPDATE_QUESTION: (setId: ID, quesId: ID) =>
      `/mcq/manage/sets/${setId}/questions/${quesId}`,
    DELETE_QUESTION: (setId: ID, quesId: ID) =>
      `/mcq/manage/sets/${setId}/questions/${quesId}`,
    REORDER_QUESTIONS: (setId: ID) =>
      `/mcq/manage/sets/${setId}/questions/reorder`,

    // CSV Import/Export endpoints
    CSV_EXPORT_SET: (id: ID) => `/mcq/csv/export/${id}`,
    CSV_IMPORT: "/mcq/csv/import",
    CSV_IMPORT_STATUS: (jobId: ID) => `/mcq/csv/import/status/${jobId}`,
    CSV_TEMPLATE: (type: string) => `/mcq/csv/template/${type}`,
  },

  // Audit endpoints (Admin)
  AUDIT: {
    ORDERS: "/admin/audit/orders",
    ORDER_BY_ID: (orderId: string) => `/admin/audit/orders/${orderId}`,
    ENROLLMENTS: "/admin/audit/enrollments",
    STUDENT_ENROLLMENTS: (studentId: string) =>
      `/admin/audit/students/${studentId}/enrollments`,
    ENROLLMENT_BY_ID: (enrollmentId: string) =>
      `/admin/audit/enrollments/${enrollmentId}`,
    PAUSE_ENROLLMENT: (enrollmentId: string) =>
      `/enrollments/${enrollmentId}/pause`,
    RESUME_ENROLLMENT: (enrollmentId: string) =>
      `/enrollments/${enrollmentId}/resume`,
    // Admin-only hard delete — audited server-side (ENROLLMENT_DELETED)
    DELETE_ENROLLMENT: (enrollmentId: string) => `/enrollments/${enrollmentId}`,
    TRANSACTIONS: "/admin/audit/payment-transactions",
    TRANSACTION_BY_ID: (transactionId: string) =>
      `/admin/audit/payment-transactions/${transactionId}`,
    LOGS: "/admin/audit/logs",
    LOG_BY_ID: (logId: string) => `/admin/audit/logs/${logId}`,
  },

  // Payment endpoints (admin/teacher only)
  PAYMENT: {
    REFUND: "/payment/refund",
    REFUNDS_FOR_ORDER: (orderId: string) => `/payment/order/${orderId}/refunds`,
    // Admin-only. Compares our record against what Razorpay actually reports.
    INSPECT_ORDER: (orderId: string) => `/payment/order/${orderId}/inspect`,
    // Admin-only. Re-checks Razorpay and grants access only if a payment was
    // genuinely captured — it cannot mark an unpaid order as paid.
    RECONCILE_ORDER: (orderId: string) => `/payment/order/${orderId}/reconcile`,
  },
} as const;
